/**
 * Boot-timeline emitter — secondary diagnostic channel.
 *
 * Ships the full boot marks array as a single PostHog event, enriched with
 * FCP and LCP from PerformanceObserver. On success, cancels the fallback
 * sendBeacon timer registered in index.html so we don't double-ship.
 *
 * Primary channel (navigator.sendBeacon → /api/boot-ping, registered in
 * index.html) exists because THIS file only runs after PostHog loads,
 * which is exactly what fails in the scenarios we're trying to diagnose.
 */

import { observePerf } from './perfObserver';
import { onLCP } from 'web-vitals/attribution';

// Two-signal flush: waits for PostHog-ready AND boot-timeline-ready.
// Flush fires when both are true. Safety fallback (below) covers the case
// where the ready signal never arrives (e.g. dashboard fails to hydrate).
let firedOnce = false;
let posthogReady = false;
let timelineReady = false;
const SAFETY_FALLBACK_MS = 15_000;
const vitals = {
  fcp_ms: null,
  lcp_ms: null,
  // Attribution from web-vitals/attribution onLCP. PostHog's native
  // $web_vitals_LCP_event ships with empty entries[] because PerformanceEntry
  // getters don't JSON-serialize — we capture flat strings/numbers here so
  // the dashboard can answer "which element is the LCP candidate?".
  lcp_element: null,
  lcp_url: null,
  lcp_ttfb_ms: null,
  lcp_resource_load_delay_ms: null,
  lcp_resource_load_duration_ms: null,
  lcp_element_render_delay_ms: null,
};

/**
 * Append a named mark to the boot-timeline array.
 * Safe to call before React mounts, in SSR contexts, or before the
 * `window.__mark` registry has loaded. All call sites across main.jsx,
 * AuthContext, and shared/api/auth should use this helper rather than
 * re-implementing the guard.
 */
export function mark(name, extra) {
  if (typeof window === 'undefined') return;
  const fn = /** @type {any} */ (window).__mark;
  if (typeof fn === 'function') fn(name, extra);
}

function readMarks() {
  if (typeof window === 'undefined') return [];
  return Array.isArray(window.__bootMarks) ? window.__bootMarks.slice() : [];
}

function startPerformanceObservers() {
  observePerf('paint', { buffered: true }, (entries) => {
    const fcp = entries.find((e) => e.name === 'first-contentful-paint');
    if (fcp) vitals.fcp_ms = Math.round(fcp.startTime);
  });
  // reportAllChanges so we capture the LATEST candidate as of flush time.
  // Caveat: the boot flush typically fires when the dashboard hydrates
  // (~1–2s in), which may be before the browser finalizes LCP. So
  // `lcp_ms` here is "LCP candidate at first hydration", not the final
  // value PostHog's $web_vitals reports on page-hide. Cross-reference
  // with $web_vitals_LCP_value for authoritative timing; use the
  // attribution fields below to identify the candidate element.
  try {
    onLCP((metric) => {
      vitals.lcp_ms = Math.round(metric.value);
      const a = metric.attribution;
      if (!a) return;
      vitals.lcp_element = a.element || null;
      vitals.lcp_url = a.url || null;
      vitals.lcp_ttfb_ms = roundOrNull(a.timeToFirstByte);
      vitals.lcp_resource_load_delay_ms = roundOrNull(a.resourceLoadDelay);
      vitals.lcp_resource_load_duration_ms = roundOrNull(a.resourceLoadDuration);
      vitals.lcp_element_render_delay_ms = roundOrNull(a.elementRenderDelay);
    }, { reportAllChanges: true });
  } catch {
    // older browsers without LCP support — leave vitals.lcp_* null
  }
}

function roundOrNull(n) {
  return n != null ? Math.round(n) : null;
}

function flushBootTimeline(reason) {
  if (typeof window === 'undefined') return;
  try {
    const marks = readMarks();
    const navAny = /** @type {any} */ (navigator);
    const conn = navAny.connection && navAny.connection.effectiveType;
    const payload = {
      reason,
      t0: typeof window.__bootT0 === 'number' ? Math.round(window.__bootT0) : null,
      marks,
      fcp_ms: vitals.fcp_ms,
      lcp_ms: vitals.lcp_ms,
      lcp_element: vitals.lcp_element,
      lcp_url: vitals.lcp_url,
      lcp_ttfb_ms: vitals.lcp_ttfb_ms,
      lcp_resource_load_delay_ms: vitals.lcp_resource_load_delay_ms,
      lcp_resource_load_duration_ms: vitals.lcp_resource_load_duration_ms,
      lcp_element_render_delay_ms: vitals.lcp_element_render_delay_ms,
      reload_count: +(sessionStorage.getItem('chunk_reload_count') || 0),
      conn: conn || null,
      ua: navigator.userAgent,
      visibility: document.visibilityState,
      build_version: import.meta.env?.VITE_BUILD_VERSION || 'dev',
    };
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('boot_timeline', payload);
    }
    // PostHog emitted successfully → the fallback sendBeacon is redundant.
    if (window.__bootPingTimer) {
      clearTimeout(window.__bootPingTimer);
      window.__bootPingTimer = null;
    }
    // Boot reached the success path; reset the reload counter so the next
    // pageload starts from a clean slate.
    try { sessionStorage.removeItem('chunk_reload_count'); } catch { /* storage unavailable */ }
  } catch {
    // Silent — diagnostic instrumentation must never break the app.
  }
}

function tryFlush(reason) {
  if (firedOnce) return;
  if (!posthogReady || !timelineReady) return;
  firedOnce = true;
  flushBootTimeline(reason);
}

/**
 * Signal that the critical boot path has completed its marks and the
 * timeline is ready to ship. Fires flush immediately if PostHog is also
 * ready; otherwise, the next poll cycle picks it up. Idempotent.
 *
 * Called today from useDashboardData after first hydration — the earliest
 * point at which every completion mark we care about has been recorded.
 */
export function notifyBootTimelineReady() {
  if (typeof window === 'undefined') return;
  timelineReady = true;
  // Reason distinguishes which gate unblocked the flush in PostHog:
  // 'timeline_ready' = dashboard was the last gate (expected happy path).
  // 'posthog_ready'  = PostHog was the last gate (PostHog loaded slowly).
  tryFlush('timeline_ready');
}

/**
 * Schedule the emitter to fire once PostHog is available AND the boot
 * timeline has been signalled ready (via notifyBootTimelineReady).
 * Called from main.jsx after the analytics loader kicks off.
 */
export function scheduleBootTimelineFlush() {
  if (typeof window === 'undefined') return;
  startPerformanceObservers();
  let attempts = 0;
  const maxAttempts = 30; // ~9 seconds of polling at 300ms
  const poll = () => {
    if (firedOnce) return;
    if (window.posthog && typeof window.posthog.capture === 'function') {
      posthogReady = true;
      tryFlush('posthog_ready');
      return;
    }
    attempts += 1;
    if (attempts >= maxAttempts) {
      // PostHog never showed — let the sendBeacon fallback handle it.
      return;
    }
    setTimeout(poll, 300);
  };
  poll();

  // Safety fallback: ship whatever we have if the ready signal never
  // arrives (e.g. dashboard errored, infinite-loading bug, user navigated
  // away). Better a truncated timeline than none.
  setTimeout(() => {
    if (firedOnce || !posthogReady) return;
    firedOnce = true;
    flushBootTimeline('safety_fallback');
  }, SAFETY_FALLBACK_MS);
}
